import { currentRequestTimeStamp } from "../../Constants";

/**
 * Maps a raw transporter object to the standard benchmark transporter entry shape.
 * @param {Object} br - Raw transporter object from API.
 * @param {boolean} [checked=false] - Whether the transporter should be pre-selected.
 * @returns {Object} Normalized transporter entry.
 */
export const buildTransporterEntry = (br, checked = false) => ({
  transporter_id: br.transporter_id,
  overall_rating: br.overall_rating,
  transporter_name: br.transporter_name,
  ischecked: checked,
  contact_information: br.contact_information || [],
  access_control: br.access_control || [],
});

/**
 * Builds a standard lane data object for DiscoverNow enquiries.
 * @param {Object} r - A single DiscoverNow route item from rspl.
 * @param {Array} tenure - Selected tenure value array.
 * @param {Array} brlist - Pre-built benchmark transporter list.
 * @returns {Object} A lane object ready to be pushed into send_data.
 */
export const buildDiscoverNowLane = (r, tenure, brlist) => ({
  module_key: "discovernow",
  lane_details: {
    origin: r.origin,
    destination: r.destination,
    drop: "oneway",
    drop_locations: [],
    truck_types: [],
    truck_required: "",
    contract_tenure: tenure,
    due_date: "",
    submission_date: "",
    capacity: "MT",
    tonnage: "0",
    product_types: [],
  },
  benchmark_transporter_list: brlist,
  terms_and_condition: {},
  disabled: false,
  is_seen: false,
  deselect_all: true,
  additional_details: "",
});

/**
 * Builds a standard lane data object for BenchmarkNow enquiries.
 * @param {Object} r - A single BenchmarkNow item from rspl.
 * @param {Array} tenure - Selected tenure value array.
 * @param {Array} brlist - Pre-built benchmark transporter list.
 * @returns {Object} A lane object ready to be pushed into send_data.
 */
export const buildBenchmarkLane = (r, tenure, brlist) => {
  const truckType = {
    label: r?.value?.truck_type,
    value: r?.value?.truck_type,
  };
  return {
    module_key: "benchmark",
    lane_details: {
      origin: r.value.origin,
      destination: r.value.destination,
      drop: "oneway",
      drop_locations: [],
      truck_types: truckType,
      truck_required: "",
      contract_tenure: tenure,
      due_date: "",
      submission_date: "",
      capacity: "MT",
      tonnage: "0",
      product_types: [],
    },
    benchmark_transporter_list: brlist,
    terms_and_condition: {},
    disabled: false,
    is_seen: false,
    deselect_all: true,
    additional_details: "",
  };
};

/**
 * Builds a standard lane data object for Planning enquiries.
 * @param {Object} item - A planning spot order / load item.
 * @param {Object} truck - The truck to allocate.
 * @param {Array} tenure - Selected tenure value array.
 * @param {Array} [spotTransporterList=[]] - User's master spot transporter list.
 * @returns {Object} A lane object ready to be pushed into send_data.
 */
export const buildPlanningLane = (item, truck, tenure, spotTransporterList = [], email) => ({
  module_key: "planning",
  lane_details: {
    origin: item.routes[0].origin,
    destination: item.routes[item.routes.length - 1].destination,
    drop: item.orders_clubbing_type === "point to point" ? "oneway" : (item.orders_clubbing_type || "oneway"),
    drop_locations: [],
    truck_types:
    {
      label: truck.truck_type,
      value: truck.truck_type,
      truck_max_volume: truck.truck_max_volume,
      truck_max_weight: truck.truck_max_weight,
    },
    truck_required: 1,
    due_date: item.dispatch_date,
    submission_date: currentRequestTimeStamp(),
    contract_tenure: tenure,
    capacity: "KG",
    tonnage: truck.truck_max_weight ? String(truck.truck_max_weight) : "0",
    product_types: [],
  },
  benchmark_transporter_list:
    spotTransporterList.length > 0 ? spotTransporterList : [],
  terms_and_condition: {},
  disabled: false,
  is_seen: false,
  deselect_all: true,
  additional_details: "",
  from: "planning",
  plan_type: item.plan_type,
  truck_id: truck.truck_id,
  load_id: item.load_id,
  email: email,
  assigned_to: email,
  assigned_by: email,
});

/**
 * Builds a standard lane data object for TrucksAvailable (adhoc) enquiries.
 * @param {Object} params
 * @returns {Object} A lane object ready to be pushed into send_data.
 */
export const buildTrucksAvailableLane = ({
  origin,
  destination,
  selectedTrucks,
  truckrequired,
  date,
  tenure,
  brlist,
  capacity,
  tonnage,
  selectedIndustry
}) => ({
  module_key: "lorri_spot",
  lane_details: {
    origin: origin,
    destination: destination,
    drop: "oneway",
    drop_locations: [],
    truck_types: selectedTrucks,
    truck_required: truckrequired,
    due_date: date,
    submission_date: "",
    contract_tenure: tenure,
    capacity: capacity,
    tonnage: tonnage ? String(tonnage) : "",
    product_types: selectedIndustry,
  },
  benchmark_transporter_list: brlist,
  terms_and_condition: {},
  disabled: false,
  is_seen: false,
  deselect_all: true,
  additional_details: "",
});

/**
 * Formats a client-side lane object into the standard API payload for create_enquiry_v2.
 * Used by both postSpotEnquiry and postPlanningEnquiry to eliminate duplicate mapping logic.
 *
 * @param {Object} lane - Client-side lane data object.
 * @param {string} defaultModuleKey - Default module key if lane.module_key is empty/undefined.
 * @returns {Object} API payload.
 */
export const buildEnquiryApiPayload = (lane, defaultModuleKey) => {
  const simplifiedTransporters = (lane.benchmark_transporter_list || [])
    .filter((t) => t.ischecked || t.isChecked)
    .map((t) => ({
      transporter_id: t.transporter_id,
      transporter_name: t.transporter_name,
      isChecked: true,
    }));

  const {
    submission_date,
    expiry_date,
    document_details,
    capacity,
    ...restLaneDetails
  } = lane.lane_details || {};

  const payload = {
    module_key: lane.module_key === "lorri_spot" || !lane.module_key
      ? defaultModuleKey
      : lane.module_key,
    lane_details: {
      ...restLaneDetails,
      tonnage: (restLaneDetails.tonnage && restLaneDetails.tonnage !== "0" && restLaneDetails.tonnage !== 0)
        ? String(restLaneDetails.tonnage)
        : "",
      sales_invoice_number: document_details,
      tonnage_unit: capacity,
      truck_types: Array.isArray(lane.lane_details?.truck_types)
        ? lane.lane_details.truck_types[0]
        : lane.lane_details?.truck_types,
      contract_tenure: Array.isArray(lane.lane_details?.contract_tenure)
        ? lane.lane_details.contract_tenure[0]
        : lane.lane_details?.contract_tenure,
    },
    benchmark_transporter_list: simplifiedTransporters,
    terms_and_condition: {
      contracted_terms: {
        value: lane.terms_and_condition?.contracted_terms?.value ?? false,
      },
      save_company_profile: {
        value: lane.terms_and_condition?.save_company_profile?.value ?? false,
      },
      policy_terms: {
        value: lane.terms_and_condition?.policy_terms?.value ?? "",
      },
      insurance: {
        value: lane.terms_and_condition?.insurance?.value ?? "",
      },
      detention_charges: {
        value: lane.terms_and_condition?.detention_charges?.value ?? "",
      },
      damages_clause: {
        value: lane.terms_and_condition?.damages_clause?.value ?? "",
      },
      shortage_clause: {
        value: lane.terms_and_condition?.shortage_clause?.value ?? "",
      },
      fuel_clause: {
        value: lane.terms_and_condition?.fuel_clause?.value ?? "",
      },
      insured_amount: {
        value: lane.terms_and_condition?.insured_amount?.value ?? "",
      },
      comments: {
        value: lane.terms_and_condition?.comments?.value ?? "",
      },
      uploaded_files: {
        user_files: lane.terms_and_condition?.uploaded_files?.user_files ?? [],
      },
    },
  };

  // Add planning/specific metadata if present on the lane object
  if (lane.load_id) payload.load_id = lane.load_id;
  if (lane.truck_id) payload.truck_id = lane.truck_id;
  if (lane.assigned_to) payload.assigned_to = lane.assigned_to;
  if (lane.assigned_by) payload.assigned_by = lane.assigned_by;
  if (lane.plan_type) payload.plan_type = lane.plan_type;

  // Add reject and open specific metadata if present on the lane object
  if (lane.previous_truck_enquiry_id) {
    payload.previous_truck_enquiry_id = lane.previous_truck_enquiry_id;
    payload.open_enquiry_transporter_id = lane.open_enquiry_transporter_id;
    payload.open_remark = lane.open_remark;
  }

  return payload;
};
